/**
 * OAuth2TokenManager - Centralized OAuth2 token management
 * 
 * CHANGE HISTORY:
 * - 251026_0835 [pending] danielbeer: Initial creation
 * 
 * Handles OAuth2 authentication flows, token storage, and automatic refresh
 */

export interface OAuth2Config {
  clientId: string;
  clientSecret?: string;
  tenantId?: string; // For Microsoft
  authUrl?: string;
  tokenUrl: string;
  scope: string | string[];
  grantType: 'client_credentials' | 'authorization_code' | 'refresh_token';
}

export interface OAuth2Token {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: number;
  refreshToken?: string;
  scope?: string;
}

export interface TokenStorage {
  get(key: string): Promise<OAuth2Token | null>;
  set(key: string, token: OAuth2Token): Promise<void>;
  delete(key: string): Promise<void>;
}

/**
 * In-memory token storage (default)
 */
class MemoryTokenStorage implements TokenStorage {
  private tokens: Map<string, OAuth2Token> = new Map();

  async get(key: string): Promise<OAuth2Token | null> {
    return this.tokens.get(key) || null;
  }

  async set(key: string, token: OAuth2Token): Promise<void> {
    this.tokens.set(key, token);
  }

  async delete(key: string): Promise<void> {
    this.tokens.delete(key);
  }
}

/**
 * OAuth2 Token Manager
 */
export class OAuth2TokenManager {
  private storage: TokenStorage;
  private refreshBuffer = 300000; // Refresh 5 minutes before expiry

  constructor(storage?: TokenStorage) {
    this.storage = storage || new MemoryTokenStorage();
  }

  /**
   * Get valid access token (auto-refresh if needed)
   */
  async getToken(key: string, config: OAuth2Config): Promise<string> {
    const token = await this.storage.get(key);

    // No token or expired
    if (!token || this.isExpired(token)) {
      const newToken = await this.requestToken(config);
      await this.storage.set(key, newToken);
      return newToken.accessToken;
    }

    // Token expiring soon, refresh if possible
    if (this.isExpiringSoon(token) && token.refreshToken && config.grantType === 'refresh_token') {
      try {
        const refreshed = await this.refreshToken(config, token.refreshToken);
        await this.storage.set(key, refreshed);
        return refreshed.accessToken;
      } catch (error) {
        console.warn('Token refresh failed, using existing token:', error);
        return token.accessToken;
      }
    }

    return token.accessToken;
  }

  /**
   * Request new token
   */
  private async requestToken(config: OAuth2Config): Promise<OAuth2Token> {
    const params = new URLSearchParams({
      client_id: config.clientId,
      grant_type: config.grantType,
    });

    if (config.clientSecret) {
      params.append('client_secret', config.clientSecret);
    }

    // Scope handling
    const scope = Array.isArray(config.scope) ? config.scope.join(' ') : config.scope;
    params.append('scope', scope);

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`OAuth2 token request failed: ${response.statusText} - ${error}`);
    }

    const data = await response.json();

    return {
      accessToken: data.access_token,
      tokenType: data.token_type || 'Bearer',
      expiresIn: data.expires_in,
      expiresAt: Date.now() + (data.expires_in * 1000),
      refreshToken: data.refresh_token,
      scope: data.scope,
    };
  }

  /**
   * Refresh token
   */
  private async refreshToken(config: OAuth2Config, refreshToken: string): Promise<OAuth2Token> {
    const params = new URLSearchParams({
      client_id: config.clientId,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    });

    if (config.clientSecret) {
      params.append('client_secret', config.clientSecret);
    }

    const response = await fetch(config.tokenUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params,
    });

    if (!response.ok) {
      throw new Error(`Token refresh failed: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      accessToken: data.access_token,
      tokenType: data.token_type || 'Bearer',
      expiresIn: data.expires_in,
      expiresAt: Date.now() + (data.expires_in * 1000),
      refreshToken: data.refresh_token || refreshToken, // Keep old if not provided
      scope: data.scope,
    };
  }

  /**
   * Check if token is expired
   */
  private isExpired(token: OAuth2Token): boolean {
    return Date.now() >= token.expiresAt;
  }

  /**
   * Check if token is expiring soon
   */
  private isExpiringSoon(token: OAuth2Token): boolean {
    return Date.now() >= (token.expiresAt - this.refreshBuffer);
  }

  /**
   * Manually set token (for pre-configured tokens)
   */
  async setToken(key: string, accessToken: string, expiresIn: number = 3600): Promise<void> {
    const token: OAuth2Token = {
      accessToken,
      tokenType: 'Bearer',
      expiresIn,
      expiresAt: Date.now() + (expiresIn * 1000),
    };
    await this.storage.set(key, token);
  }

  /**
   * Clear token
   */
  async clearToken(key: string): Promise<void> {
    await this.storage.delete(key);
  }

  /**
   * Get token info (without auto-refresh)
   */
  async getTokenInfo(key: string): Promise<OAuth2Token | null> {
    return await this.storage.get(key);
  }
}

export default OAuth2TokenManager;
